/**
 * Store profile / cover API.
 *
 * Everything that shapes a merchant's public storefront (gestockweb): identity,
 * location, business lines, order contact, WhatsApp number, delivery
 * instructions and the SPEAKON-generated cover banner.
 */

import apiClient from "../api";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const unwrap = (error: any, fallback: string) =>
  error?.response?.data || error || { message: fallback };

export interface BusinessLine {
  key: string;
  label: string;
  description?: string;
}

export interface StoreProfile {
  store: { storeId: string; name: string; category: string };
  businessLines: BusinessLine[];
  productTypes: { type: string; label: string; unitLabel: string }[];
  summary?: string;
}

export interface CoverResult {
  message?: string;
  cover: {
    imageUrl: string;
    imageKey: string;
    imagePreviewUrl: string;
    model?: string;
    quality?: string;
  };
  copy: { headline?: string; subtitle?: string; callToAction?: string } | null;
  store: { id: string; coverImageUrl: string; coverImageKey: string };
}

export interface StoreProfilePayload {
  name?: string;
  city?: string;
  address?: string;
  description?: string;
  category?: string;
  serviceArea?: string;
  websiteSummary?: string;
  publicOrderContactName?: string;
  publicOrderWhatsappNumber?: string;
  publicOrderInstructions?: string;
  coverImageUrl?: string;
  coverImageKey?: string;
  serviceHighlights?: string[];
}

export const storeProfileApi = {
  /** Catalog profile of the active (or given) store. */
  profile: async (storeId?: string): Promise<StoreProfile> => {
    try {
      const response = await apiClient.get("gestock/ai/catalog/profile", {
        headers: authHeaders(),
        params: storeId ? { storeId } : undefined,
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger le profil boutique.");
    }
  },

  /** Update the public-facing store profile. */
  update: async (storeId: string, payload: StoreProfilePayload) => {
    try {
      const response = await apiClient.patch(
        `gestock/stores/${storeId}/public-profile`,
        payload,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Mise à jour impossible.");
    }
  },

  /** SPEAKON AI generates a cover banner from the store's full profile. */
  generateCover: async (storeId: string, styleId?: string): Promise<CoverResult> => {
    try {
      const response = await apiClient.post(
        `gestock/stores/${storeId}/cover/generate`,
        { styleId },
        { headers: authHeaders(), timeout: 180000 }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Génération de la couverture impossible.");
    }
  },

  /** Presigned read URL for a store cover (the S3 bucket is private). */
  readCover: async (imageKey: string): Promise<string> => {
    try {
      const response = await apiClient.get(
        "gestock/uploads/vehicle-cover-image/read",
        {
          headers: authHeaders(),
          params: { key: imageKey },
        }
      );
      return String(response.data?.imageUrl || response.data?.url || "");
    } catch (error) {
      throw unwrap(error, "Lecture de la couverture impossible.");
    }
  },
};

export default storeProfileApi;